import { Loc, Text } from 'main.core';
import QRCode from 'qrcode';
import { mapState, mapWritableState, mapActions } from 'ui.vue3.pinia';

import {
	CaptchaAnalytics,
	CaptchaScenarios,
	CaptchaTypes,
} from 'b24network.vue.adaptivecaptcha';
import {
	tooltip,
	BasePopup,
	IconButton,
	QrScanForm,
	TextButton,
	LegalLinks,
	AccountList,
	IconButtonType,
	LoginEnterForm,
	TextButtonType,
	PasswordEnterForm,
} from 'b24network.vue.base-components';
import {
	HtmlToTextConverter,
	SharedApi,
	UserAccount,
	UserRedirect,
	CONFIRMATION_SCENARIO_FIRST_AUTH,
	SocialServices,
	LoginValidator,
	SharedAnalytics,
	AbsoluteUrlValidator,
} from 'b24network.vue.cross-scenario-shared-js';

import { Ajax } from '../../api/ajax';
import { Analytics } from '../../api/analytics';
import { useLoggingInStore } from '../../pinia/store';
import { RoutePaths } from '../../routing/route-paths';
import { isPasswordRestoreCaptchaPrefetchError } from '../../services/password-restore-service';

import '../../../css/components/pages/logging-in.css';

// Workaround for Bitrix QRCode that doesn't support ESM and loads into global context

export const LoggingIn = {
	name: 'LoggingIn',

	pullUnsubscribe: null,

	// A class containing reusable social services specific logic
	socServicesHelper: null,

	components: {
		BasePopup,
		QrScanForm,
		IconButton,
		TextButton,
		LegalLinks,
		AccountList,
		LoginEnterForm,
		PasswordEnterForm,
	},

	directives: {
		tooltip,
	},

	data(): Object
	{
		return {
			password: '',
			loginError: '',
			passwordError: '',
			popupAnchorRect: null,
			currentAccountId: -1,
			areAdsChecked: false,
			isQrCodeVisible: false,
			isQrPopupVisible: false,
			isRememberMeForQr: false,
			extraSocialsButton: null,
			accountSocialSvcTitle: '',
			areWebinarsChecked: false,
			isRememberMeChecked: true,
			isAuthenticatedByQr: false,
			isAccountsListVisible: false,
			extraSocialsButtonRect: null,
			isProcessingLoginEnter: false,
			isProcessingVibeRegister: false,
			isProcessingPasswordEnter: false,
			isProcessingForgotPassword: false,
			socialNotificationText: '',
			isExtraSocialsPopupVisible: false,
			isSocialAccountListVisible: false,
			isUserHasEmail: false,

			isWebLoginCaptchaRequired: false,
			webLoginCaptchaError: '',
			webLoginCaptchaResult: null,
			isWebLoginCaptchaFailed: false,
			showGoToBoxBlock: false,
		};
	},

	computed: {
		...mapWritableState(useLoggingInStore, [
			'login',
			'loggingInUserId',
			'accountsHistory',
			'typeOfCaptchaOnPage',
			'isPageContentHidden',
			'navigationButtonType',
			'redirectUrlWhenAuthorized',
			'isNonSsoLoggingInForSsoPortal',
			'passwordSetUpUserDetails',
			'userCheckWord',
		]),

		...mapState(useLoggingInStore, [
			'helpLink',
			'legalLinks',
			'qrCodeConfig',
			'enteredPortal',
			'defaultCountry',
			'isPhoneEnabled',
			'forceSsoSignIn',
			'socialServices',
			'currentLanguageId',
			'passwordRestoreLogin',
			'isSigningInToSsoPortal',
			'redirectToPortalsListWhenAuthorized',
			'shouldShowCreateButtonOnAuthPage',
			'isDesktopLogin',
			'showVibeAuthPromoBlock',
			'vibecodeContinueTermsHtml',
			'legalConsentSign',
			'shouldShowPortalHostInTitle',
			'isRedirectAfterScanQrAllowed',
			'loggedInUserAccount',
			'captchaSettings',
			'webLoginCaptchaSettings',
		]),

		webLoginCaptchaSettingsForPasswordEnterForm(): ?Object
		{
			return this.isWebLoginCaptchaRequired ? this.webLoginCaptchaSettings : null;
		},

		legalLinksForCaptchaOnPage(): Object
		{
			if (!this.isWebLoginCaptchaRequired)
			{
				return this.legalLinks;
			}

			return {
				...this.legalLinks,
				captchaPrivacyPolicy: this.webLoginCaptchaSettings.captchaPrivacyPolicy
					?? this.legalLinks.captchaPrivacyPolicy,
				captchaTermsOfUsage: this.webLoginCaptchaSettings.captchaTermsOfUsage
					?? this.legalLinks.captchaTermsOfUsage,
			};
		},

		isLoginEnterStep(): boolean
		{
			return !this.currentAccount || this.isSocialServiceCurrentAccount;
		},

		isPasswordEnterStep(): boolean
		{
			return this.currentAccount && !this.isSocialServiceCurrentAccount;
		},

		visibleSocialServices(): Array
		{
			return this.socServicesHelper.findVisibleSocialServices();
		},

		socialServicesForPopup(): Array
		{
			return this.socServicesHelper.findSocialServicesForPopup();
		},

		popupWidthForExtraSocialServices(): number
		{
			return this.socServicesHelper.calculateExtraSocialServicesPopupWidth();
		},

		showMoreSocServicesButton(): boolean
		{
			return this.socServicesHelper.needShowMoreSocServicesButton();
		},

		hasAccounts(): boolean
		{
			return this.accountsHistory.length > 0;
		},

		hasMultipleAccounts(): boolean
		{
			return this.accountsHistory.length > 1;
		},

		currentAccount(): ?UserAccount
		{
			return this.findAccountInHistoryById(this.currentAccountId);
		},

		isSocialServiceCurrentAccount(): boolean
		{
			return this.currentAccount?.isSocialServiceAccount() ?? false;
		},

		currentSocialAccounts(): Array
		{
			if (this.isSocialServiceCurrentAccount)
			{
				return [this.currentAccount.toDto()];
			}

			return [];
		},

		socialSvcAccountTitle(): string
		{
			return Loc.getMessage(
				'EXT_B24NET_LOGGING_IN_EMAIL_IS_LINKED_WITH_SOC_SERV',
				{
					'[br]': '<br>',
				},
			);
		},

		socialSvcAccountDescription(): string
		{
			return Loc.getMessage(
				'EXT_B24NET_LOGGING_IN_USE_SOC_SERV_FOR_ENTER',
				{
					'[br]': '<br>',
				},
			);
		},

		loginEnterFormTitleText(): string
		{
			if (this.isPhoneEnabled)
			{
				return Loc.getMessage('EXT_B24NET_LOGGING_IN_EMAIL_OR_PHONE');
			}

			return Loc.getMessage('EXT_B24NET_LOGGING_IN_EMAIL_ONLY');
		},

		signInHeadingText(): string
		{
			if (this.enteredPortal?.host && this.shouldShowPortalHostInTitle)
			{
				const safePortalHost = Text.encode(this.enteredPortal.host);

				return Loc.getMessage(
					'EXT_B24NET_LOGGING_IN_TO_PORTAL',
					{
						'[b]': `<b title="${safePortalHost}">`,
						'[/b]': '</b>',
						'#PORTAL_NAME#': safePortalHost,
					},
				);
			}

			return Loc.getMessage('EXT_B24NET_LOGGING_IN_TITLE');
		},

		accountsHistoryDtos(): Array
		{
			return this.accountsHistory.map((account) => account.toDto());
		},

		isPopupShown(): ?Object
		{
			return this.isAccountsListVisible
				|| this.isSocialAccountListVisible
				|| this.isQrPopupVisible
			;
		},
	},

	watch: {
		login(newValue): void
		{
			this.loginError = '';
		},

		loginError(newValue): void
		{
			if (newValue && !this.showGoToBoxBlock && !this.showVibeAuthPromoBlock)
			{
				Analytics.emitShowLoginPopupBox();
				this.showGoToBoxBlock = true;
			}
		},

		password(newValue): void
		{
			this.passwordError = '';
		},

		isLoginEnterStep(isShown): void
		{
			if (isShown)
			{
				SharedAnalytics.emitSocialServiceNotificationShown();
			}
		},

		isPasswordEnterStep(isShown): void
		{
			if (isShown)
			{
				Analytics.emitShowPasswordEnterPageEvent();
			}
		},

		isPopupShown(newValue): void
		{
			this.isPageContentHidden = newValue;
		},
	},

	created(): void
	{
		this.socServicesHelper = new SocialServices(this.socialServices, false);
	},

	mounted(): void
	{
		document.title = HtmlToTextConverter.convert(this.signInHeadingText);
		this.navigationButtonType = this.shouldShowCreateButtonOnAuthPage ? 'create-portal' : '';

		this.mountQrCode(this.$refs.qrCode);
		this.mountQrCode(this.$refs.qrCodeInPopup);
		this.subscribeToQrAuthPullEvent();

		if (this.passwordRestoreLogin !== '')
		{
			this.login = this.passwordRestoreLogin;
			this.$router.replace(RoutePaths.PASS_RESTORE);

			return;
		}

		if (this.needRedirectToPasswordSetup())
		{
			this.$router.replace(RoutePaths.PASS_SETUP);

			return;
		}

		if (this.redirectToPortalsListWhenAuthorized)
		{
			UserRedirect.redirectToPortalList();
		}
		else if (this.forceSsoSignIn || this.isSigningInToSsoPortal)
		{
			this.$router.replace(RoutePaths.SSO_SIGN_IN);
		}
		else if (this.login)
		{
			this.tryEnterByLogin(true);
		}
		else
		{
			Analytics.emitShowLoginEnterPageEvent();
		}
	},

	beforeUnmount(): void
	{
		this.unSubscribeFromQrAuthPullEvent();
	},

	methods: {
		...mapActions(useLoggingInStore, [
			'isAccountConfirmed',
			'hasAccountInHistory',
			'updateAccountProperty',
			'updateCurrentUserPortals',
			'findAccountInHistoryById',
			'prependRawAccountToHistory',
			'findAccountsInHistoryExcludingOne',
			'requestPasswordRestorePrefetch',
			'clearPasswordRestorePrefetch',
		]),

		mountQrCode(qrCodeHtmlElement: HTMLElement): void
		{
			const url = `https://b24.to/a/${this.qrCodeConfig.siteId}/`
				+ `${this.qrCodeConfig.uniqueId}/${this.qrCodeConfig.channelTag}/`
			;

			new window.QRCode(qrCodeHtmlElement, {
				text: url,
				width: 220,
				height: 220,
				colorDark: '#000000',
				colorLight: '#ffffff',
			});

			// Making sure that QR code sliding animation is performed after page animation ended
			window.setTimeout(() => {
				this.isQrCodeVisible = true;
			}, 1200);

			Analytics.emitShowQrCodeEvent();
		},

		onLoginChange(newLogin): void
		{
			this.login = newLogin;
		},

		onSsoClick(): void
		{
			this.login = '';
			this.$router.replace(RoutePaths.SSO_SIGN_IN);
		},

		onSocialButtonClick(buttonType): void
		{
			this.isExtraSocialsPopupVisible = false;
			const socServiceId = IconButtonType.getSocialServiceIdByButtonType(buttonType);
			const socialText = this.socServicesHelper.getSocialServiceNotificationText(socServiceId);

			if (socialText === '')
			{
				this.socServicesHelper.tryShowSocialServiceWindow(socServiceId);
			}
			else
			{
				this.socialNotificationText = socialText;
			}
		},

		onAdsChange(areChecked): void
		{
			this.areAdsChecked = areChecked;
		},

		onWebinarsChange(areChecked): void
		{
			this.areWebinarsChecked = areChecked;
		},

		async onLoginContinueClick(): void
		{
			this.loginError = LoginValidator.validateLogin(this.login, this.isPhoneEnabled);

			if (this.loginError.length > 0)
			{
				return;
			}

			if (this.enteredPortal.ssoEnabled)
			{
				this.login = '';
				this.isNonSsoLoggingInForSsoPortal = true;
				this.$router.replace(RoutePaths.SSO_SIGN_IN);
			}
			else
			{
				Analytics.emitOnAuthorizationLoginEntered(this.login);
				this.isProcessingLoginEnter = true;
				this.tryEnterByLogin();
			}
		},

		async onVibeRegisterClick(): void
		{
			const login = this.login.trim();

			if (login === '')
			{
				this.loginError = Loc.getMessage('EXT_B24NET_LOGGING_IN_FILL_MANDATORY_FIELD');

				return;
			}

			if (!LoginValidator.isEmailLogin(login))
			{
				this.loginError = Loc.getMessage('EXT_B24NET_LOGGING_IN_VIBE_REGISTER_EMAIL_ONLY');

				return;
			}

			this.isProcessingVibeRegister = true;

			try
			{
				const accountResponse = await SharedApi.loadAccountsByLogin(
					login,
					this.defaultCountry,
					{},
					'Y',
				);

				if (accountResponse.data?.redirectUrl)
				{
					UserRedirect.redirectToUrl(accountResponse.data.redirectUrl);

					return;
				}

				if (accountResponse.data?.[0])
				{
					this.loginError = Loc.getMessage('EXT_B24NET_LOGGING_IN_VIBE_REGISTER_USER_EXISTS');

					return;
				}

				const response = await Ajax.registerNewAccountByEmailWithoutPortal(
					login,
					this.legalConsentSign,
				);

				if (response.data?.redirect)
				{
					UserRedirect.redirectToUrl(response.data.redirect);

					return;
				}

				if (this.redirectUrlWhenAuthorized !== null)
				{
					UserRedirect.redirectToUrl(this.redirectUrlWhenAuthorized);

					return;
				}

				window.location.reload();
			}
			catch (errorResponse)
			{
				this.updateLoginError(errorResponse);
			}
			finally
			{
				this.isProcessingVibeRegister = false;
			}
		},

		/**
		 * @arg {Boolean} hasSlidingAnimation - Indicates that page has active sliding animation
		 */
		async tryEnterByLogin(hasSlidingAnimation: Boolean = false): void
		{
			try
			{
				const foundUserData = await this.getUserByLogin();

				if (foundUserData && !(await this.redirectToEmailConfirmationIfNeed(foundUserData)))
				{
					this.updateAccounts(foundUserData, hasSlidingAnimation);
				}
			}
			catch (errorResponse)
			{
				this.updateLoginError(errorResponse);
			}
			finally
			{
				this.isProcessingLoginEnter = false;
			}
		},

		async getUserByLogin(): ?Object
		{
			const accountResponse = await SharedApi.loadAccountsByLogin(
				this.login,
				this.defaultCountry,
				{},
				'Y',
			);

			if (accountResponse.data?.redirectUrl)
			{
				UserRedirect.redirectToUrl(accountResponse.data.redirectUrl);

				return null;
			}

			const foundUserData = accountResponse.data[0] ?? null;

			if (!foundUserData)
			{
				this.updateNotFoundAccountError();

				return null;
			}

			return foundUserData;
		},

		hasCheckword(): boolean
		{
			return UserAccount.getCheckword() !== null;
		},

		redirectToEmailConfirmationIfNeed(foundUserData: object): boolean
		{
			const needSetPassword = foundUserData.NEED_SET_PASSWORD;
			const isSocialAuth = foundUserData.EXTERNAL_AUTH_ID === 'socservices';

			if (!isSocialAuth && needSetPassword && !this.hasCheckword())
			{
				UserRedirect.redirectToConfirmationForm({
					subject: this.login,
					scenario: CONFIRMATION_SCENARIO_FIRST_AUTH,
				});

				return true;
			}

			return false;
		},

		updateNotFoundAccountError(): void
		{
			const isEmail = LoginValidator.isEmailLogin(this.login);
			this.loginError = isEmail
				? Loc.getMessage('EXT_B24NET_LOGGING_IN_NOT_FOUND_EMAIL')
				: Loc.getMessage('EXT_B24NET_LOGGING_IN_NOT_FOUND_PHONE')
			;
			Analytics.emitLoginNotFoundEvent(!isEmail);
		},

		updateLoginError(errorResponse): void
		{
			if (errorResponse.errors?.length)
			{
				this.loginError = errorResponse.errors[0].message;
			}
			else
			{
				this.loginError = Loc.getMessage('EXT_B24NET_LOGGING_IN_UNKNOWN_ERROR_SIGN_IN');
			}
		},

		/**
		 * @arg {Boolean} waitSlidingStop - Should social service account popup wait sliding end or not
		 */
		updateAccounts(foundRawAccount: Object, waitSlidingStop: Boolean = false): void
		{
			this.accountSocialSvcTitle = '';
			this.updateWebLoginCaptchaState(foundRawAccount);

			if (this.findAccountInHistoryById(Number(foundRawAccount.ID)) === null)
			{
				this.prependRawAccountToHistory(foundRawAccount);
			}

			if (foundRawAccount.EXTERNAL_AUTH_ID === 'socservices')
			{
				Analytics.emitSocialServiceEmailErrorEvent();

				const account = this.findAccountInHistoryById(Number(foundRawAccount.ID));
				// Social services accounts have weird logins like "YA_1697660314"
				account.changeLogin(this.login);
				this.showPopupWithSocialServices(account.toSocialServiceTitle(), waitSlidingStop);
			}
			// From back-end we receive normalized phone number
			else if (foundRawAccount.FOUND_BY_PHONE === true)
			{
				this.login = foundRawAccount.LOGIN;
			}

			this.currentAccountId = foundRawAccount.ID;
			this.isUserHasEmail = foundRawAccount.EMAIL !== '' && foundRawAccount.EMAIL !== null;
		},

		updateWebLoginCaptchaState(foundRawAccount: Object): void
		{
			this.webLoginCaptchaError = '';
			this.webLoginCaptchaResult = null;
			this.isWebLoginCaptchaFailed = false;
			const isCaptchaRequired = foundRawAccount.WEB_LOGIN_CAPTCHA_REQUIRED
				?? Boolean(foundRawAccount.CAPTCHA)
			;
			this.isWebLoginCaptchaRequired = Boolean(isCaptchaRequired)
				&& this.webLoginCaptchaSettings.isEnabled === true
			;

			if (!this.isWebLoginCaptchaRequired)
			{
				this.typeOfCaptchaOnPage = '';

				return;
			}

			this.typeOfCaptchaOnPage = this.webLoginCaptchaSettings.captchaType === CaptchaTypes.BITRIX
				? ''
				: this.webLoginCaptchaSettings.captchaType
			;
			CaptchaAnalytics.emitCaptchaShowEvent(
				this.webLoginCaptchaSettings.captchaType,
				CaptchaScenarios.WEB_LOGIN,
			);
		},

		showPopupWithSocialServices(socialServiceTitle: string, needDelay: Boolean = false): void
		{
			const delayMsec = needDelay ? 1000 : 0;

			/**
			 * Because popup is positioned relative to heading we need waiting sliding animation end
			 * to make sure the popup appears in proper horizontal position
			 */
			window.setTimeout(() => {
				this.popupAnchorRect = this.$refs.popupAnchor?.getBoundingClientRect() ?? null;
				this.isSocialAccountListVisible = true;
				this.accountSocialSvcTitle = socialServiceTitle;
			}, delayMsec);
		},

		onOtherSocialsClick(buttonNode): void
		{
			this.extraSocialsButtonRect = buttonNode?.getBoundingClientRect();
			this.isExtraSocialsPopupVisible = true;
		},

		onExtraSocialsPopupClose(): void
		{
			this.isExtraSocialsPopupVisible = false;
		},

		onB24CreateClick(): void
		{
			Analytics.emitCreateB24ButtonClick();
			UserRedirect.redirectToPortalCreation();
		},

		onPasswordChange(newPassword): void
		{
			this.password = newPassword;
		},

		onPasswordContinueClick(): void
		{
			if (this.isProcessingPasswordEnter)
			{
				return;
			}

			this.isProcessingPasswordEnter = true;
			this.tryAuthenticate();
		},

		async tryAuthenticate(): void
		{
			const responseType = (new URL(window.location.href))
				.searchParams
				.get('response_type')
			;
			const captchaResult = this.webLoginCaptchaResult;

			try
			{
				await Ajax.authenticate(
					this.login,
					this.password,
					this.isRememberMeChecked,
					null,
					captchaResult,
				);

				SharedAnalytics.emitAuthSuccess();

				if (this.redirectUrlWhenAuthorized !== null)
				{
					UserRedirect.redirectToUrl(this.redirectUrlWhenAuthorized);
				}
				else if (responseType === 'code')
				{
					window.location.reload();
				}
				else
				{
					UserRedirect.redirectToPortalList();
				}
			}
			catch (errorResponse)
			{
				const errorCode = errorResponse?.errors?.[0]?.code ?? '';

				if (errorCode === 'ERROR_CAPTCHA')
				{
					await this.handleWebLoginCaptchaError(errorResponse);
				}
				else
				{
					const errorCustomData = this.getErrorCustomData(errorResponse);
					this.webLoginCaptchaError = '';
					this.updatePasswordError(errorResponse);

					if (captchaResult !== null)
					{
						this.webLoginCaptchaResult = null;
						this.isWebLoginCaptchaFailed = false;
						await this.resetWebLoginCaptchaProvider();
					}

					if (errorCustomData.webLoginCaptchaRequired === true)
					{
						await this.showWebLoginCaptchaAfterPasswordError();
					}
				}
			}
			finally
			{
				this.isProcessingPasswordEnter = false;
			}
		},

		async showWebLoginCaptchaAfterPasswordError(): Promise<void>
		{
			if (this.webLoginCaptchaSettings.isEnabled !== true)
			{
				return;
			}

			const wasCaptchaRequired = this.isWebLoginCaptchaRequired;
			this.isWebLoginCaptchaRequired = true;
			this.webLoginCaptchaError = '';
			this.webLoginCaptchaResult = null;
			this.isWebLoginCaptchaFailed = false;
			this.typeOfCaptchaOnPage = this.webLoginCaptchaSettings.captchaType === CaptchaTypes.BITRIX
				? ''
				: this.webLoginCaptchaSettings.captchaType
			;

			if (!wasCaptchaRequired)
			{
				CaptchaAnalytics.emitCaptchaShowEvent(
					this.webLoginCaptchaSettings.captchaType,
					CaptchaScenarios.WEB_LOGIN,
				);
			}

			if (
				!wasCaptchaRequired
				&& this.webLoginCaptchaSettings.captchaType === CaptchaTypes.BITRIX
			)
			{
				await this.resetWebLoginCaptchaProvider();

				return;
			}

			await this.$nextTick();
		},

		async handleWebLoginCaptchaError(errorResponse): Promise<void>
		{
			const wasCaptchaRequired = this.isWebLoginCaptchaRequired;
			const captchaError = errorResponse?.errors?.[0]?.message
				|| Loc.getMessage('EXT_B24NET_ADAPTIVE_CAPTCHA_FAILED')
			;
			this.isWebLoginCaptchaRequired = true;
			this.webLoginCaptchaError = '';
			this.webLoginCaptchaResult = null;
			this.isWebLoginCaptchaFailed = true;
			this.typeOfCaptchaOnPage = this.webLoginCaptchaSettings.captchaType === CaptchaTypes.BITRIX
				? ''
				: this.webLoginCaptchaSettings.captchaType
			;
			await this.$nextTick();
			this.webLoginCaptchaError = captchaError;

			if (!wasCaptchaRequired)
			{
				CaptchaAnalytics.emitCaptchaShowEvent(
					this.webLoginCaptchaSettings.captchaType,
					CaptchaScenarios.WEB_LOGIN,
				);
			}

			await this.resetWebLoginCaptchaProvider();

			if (!wasCaptchaRequired)
			{
				this.$refs.passwordEnterForm?.focusCaptcha();
			}
		},

		async resetWebLoginCaptchaProvider(): Promise<void>
		{
			await this.$nextTick();

			try
			{
				await this.$refs.passwordEnterForm?.resetCaptcha();
			}
			catch
			{
				// The authentication error remains the only user-facing error.
			}
		},

		updatePasswordError(errorResponse): void
		{
			if (errorResponse.errors?.length)
			{
				const error = errorResponse.errors[0];
				if (error.code === 'AUTH_ERROR')
				{
					const errorCustomData = this.getErrorCustomData(errorResponse);

					if (errorCustomData.isCaptchaError)
					{
						this.passwordError = '';
					}
					else
					{
						this.passwordError = error.message ?? Loc.getMessage('EXT_B24NET_LOGGING_IN_INVALID_PASSWORD');
						Analytics.emitWrongPasswordErrorEvent();
					}
				}
				else
				{
					this.passwordError = error.message ?? Loc.getMessage('EXT_B24NET_LOGGING_IN_INVALID_PASSWORD');
				}
			}
			else if (errorResponse instanceof Error)
			{
				console.error(errorResponse);
			}
		},

		getErrorCustomData(errorResponse): Object
		{
			return errorResponse?.errors?.[0]?.customData ?? {};
		},

		async onForgotPasswordClick(): Promise<void>
		{
			if (this.isProcessingForgotPassword)
			{
				return;
			}

			this.isProcessingForgotPassword = true;

			try
			{
				const prefetch = await this.requestPasswordRestorePrefetch({
					login: this.login,
					defaultCountry: this.defaultCountry,
				});
				this.login = prefetch.login;
				Analytics.emitSendEmailRestorePasswordEvent(prefetch.account?.id ?? null);
				this.$router.replace(RoutePaths.PASS_RESTORE);
			}
			catch (error)
			{
				if (isPasswordRestoreCaptchaPrefetchError(error))
				{
					this.clearPasswordRestorePrefetch();
					this.$router.replace(RoutePaths.PASS_RESTORE);
				}
				else
				{
					const errorResponse = error;
					this.passwordError = errorResponse?.errors?.[0]?.message
						?? errorResponse?.message
						?? Loc.getMessage('EXT_B24NET_LOGGING_IN_UNKNOWN_ERROR_SIGN_IN')
					;
					this.clearPasswordRestorePrefetch();
				}
			}
			finally
			{
				this.isProcessingForgotPassword = false;
			}
		},

		onRememberMeChange(isChecked): void
		{
			this.isRememberMeChecked = isChecked;
		},

		onAccountChangeClick(): void
		{
			this.popupAnchorRect = this.$refs.popupAnchor?.getBoundingClientRect() ?? null;
			this.isAccountsListVisible = true;
			Analytics.emitShowExistingAccountsListPageEvent();
		},

		async onAccountItemClick(accountId): void
		{
			if (this.isProcessingPasswordEnter)
			{
				return;
			}

			const selectedAccount = this.findAccountInHistoryById(accountId);
			if (!selectedAccount)
			{
				return;
			}

			this.isAccountsListVisible = false;
			this.isProcessingPasswordEnter = true;

			try
			{
				const accountResponse = await SharedApi.loadAccountsByLogin(
					selectedAccount.toLogin(),
					this.defaultCountry,
					{},
					'Y',
				);
				if (accountResponse.data?.redirectUrl)
				{
					UserRedirect.redirectToUrl(accountResponse.data.redirectUrl);

					return;
				}

				const accounts = Array.isArray(accountResponse.data) ? accountResponse.data : [];
				const foundRawAccount = accounts.find((account) => {
					return Number(account.ID) === Number(accountId);
				}) ?? null;
				if (!foundRawAccount)
				{
					throw new Error(Loc.getMessage('EXT_B24NET_LOGGING_IN_UNKNOWN_ERROR_SIGN_IN'));
				}

				this.currentAccountId = accountId;
				this.updateWebLoginCaptchaState(foundRawAccount);
				await this.$nextTick();
				if (this.isWebLoginCaptchaRequired)
				{
					await this.resetWebLoginCaptchaProvider();
				}
				this.login = selectedAccount.toLogin();
				this.password = '';
			}
			catch (errorResponse)
			{
				this.passwordError = errorResponse?.errors?.[0]?.message
					?? errorResponse?.message
					?? Loc.getMessage('EXT_B24NET_LOGGING_IN_UNKNOWN_ERROR_SIGN_IN')
				;
			}
			finally
			{
				this.isProcessingPasswordEnter = false;
			}
		},

		onAccountListPopupClose(): void
		{
			this.isAccountsListVisible = false;
		},

		async onQrAuthPullEvent(params): void
		{
			if (!params.token)
			{
				return;
			}

			try
			{
				const response = await Ajax.authenticateByQrCode(params.token, this.isRememberMeForQr);
				this.handleQrAuthResponse(response, params);
			}
			// eslint-disable-next-line unicorn/prefer-optional-catch-binding
			catch (errorResponse)
			{
				// intentionally doing nothing
			}
		},

		subscribeToQrAuthPullEvent(): void
		{
			if (!this.qrCodeConfig.pullConfig)
			{
				return;
			}

			const pullClient = this.$Bitrix.PullClient.get();
			this.pullUnsubscribe = pullClient.subscribe({
				moduleId: 'main',
				command: 'qrAuthorize',
				callback: this.onQrAuthPullEvent.bind(this),
			});
			pullClient.start(this.qrCodeConfig.pullConfig);
		},

		unSubscribeFromQrAuthPullEvent(): void
		{
			if (this.pullUnsubscribe)
			{
				this.pullUnsubscribe();
				this.pullUnsubscribe = null;
			}
		},

		handleQrAuthResponse(response: Object, qrAuthParams): void
		{
			if (response.status !== 'success')
			{
				return;
			}

			this.isAuthenticatedByQr = true;

			if (
				this.isRedirectAfterScanQrAllowed
				&& AbsoluteUrlValidator.isValid(qrAuthParams?.redirectUrl)
			)
			{
				Analytics.emitQrCodeAuthSuccessEvent(qrAuthParams.redirectUrl);
				// Making sure redirect is performed after QR code success auth animation
				window.setTimeout(() => UserRedirect.redirectToUrl(qrAuthParams.redirectUrl), 500);
			}
			else
			{
				Analytics.emitQrCodeAuthSuccessEvent();
				// Making sure redirect is performed after QR code success auth animation
				window.setTimeout(() => window.location.reload(), 500);
			}
		},

		getButtonTypeForSocialServiceId(socialServiceId: string): string
		{
			return IconButtonType.getButtonTypeBySocialServiceId(socialServiceId);
		},

		onIndicateB24AddressClick(): void
		{
			Analytics.emitClickLoginPopupBox();
			this.$router.replace(RoutePaths.SIGN_IN_TO_PORTAL);
		},

		onSocialAccountListPopupClose(): void
		{
			this.isSocialAccountListVisible = false;
		},

		onSocialAccountItemClick(accountId): void
		{
			const account = this.findAccountInHistoryById(accountId);

			if (account)
			{
				this.socServicesHelper.tryShowSocialServiceWindow(account.toSocialServiceId());
			}
		},

		async onDeleteAccountFromHistoryClick(accountId): void
		{
			try
			{
				const response = await SharedApi.deleteAccountFromHistory(accountId);

				if (response.data)
				{
					this.accountsHistory = this.findAccountsInHistoryExcludingOne(accountId);

					// Going back to login entering step
					if (accountId === this.currentAccountId || this.accountsHistory.length === 0)
					{
						this.login = '';
						this.currentAccountId = -1;
						this.isAccountsListVisible = false;
					}
				}
			}
			// eslint-disable-next-line unicorn/prefer-optional-catch-binding
			catch (errorResponse)
			{
				// Intentionally do noting
			}
		},

		onQrRememberMeChange(isChecked): void
		{
			this.isRememberMeForQr = isChecked;
		},

		onMobileAppStoreClick({ isIos, clientRect }): void
		{
			Analytics.emitClickOnMobileAppIcon(isIos);
		},

		onWebLoginCaptchaResultChange(captchaResult): void
		{
			const isEmptyResult = captchaResult === null
				|| captchaResult === undefined
				|| captchaResult?.bitrixCaptchaValue === ''
			;
			if (isEmptyResult)
			{
				this.webLoginCaptchaResult = null;

				return;
			}

			this.webLoginCaptchaError = '';
			this.isWebLoginCaptchaFailed = false;
			this.webLoginCaptchaResult = captchaResult;
		},

		onQrBtnClick(): void
		{
			this.isQrPopupVisible = true;
		},

		onQrPopupClose(): void
		{
			this.isQrPopupVisible = false;
		},

		onCreatePasswordFromSocServClick()
		{
			this.onForgotPasswordClick();
			this.onSocialAccountListPopupClose();
		},

		needRedirectToPasswordSetup(): boolean
		{
			const account = this.loggedInUserAccount;

			if (account && account.toId())
			{
				const checkword = UserAccount.getCheckword();
				if (checkword)
				{
					this.passwordSetUpUserDetails = {
						id: account.toId(),
						login: account.toLogin(),
						hasPassword: false,
					};
					this.userCheckWord = checkword;
					UserAccount.setCheckword();

					return true;
				}
			}

			return false;
		},
	},

	template: `
		<div
			class="b24net-logging-in"
			data-testid="b24network-logging-in-root"
		>
			<div ref="popupAnchor" class="b24net-logging-in__popup-anchor"></div>

			<div class="b24net-island-with-sidebar">
				<div class="b24net-island-with-sidebar__island">
					<div class="b24net-logging-in__island-wrapper b24net-island">
						<h2
							v-if="signInHeadingText"
							v-html="signInHeadingText"
							class="b24net-island-title"
						></h2>

						<LoginEnterForm
							v-if="isLoginEnterStep"
							class="b24net-logging-in__login-form"
							:login-value="login"
							:show-sso-button="false"
							:show-qr-code-button="true"
							:show-confirmations="false"
							:login-error-text="loginError"
							:show-user-agreement-note="false"
							:is-waiting="isProcessingLoginEnter"
							:is-vibe-register-waiting="isProcessingVibeRegister"
							:social-services="visibleSocialServices"
							:input-title-text="loginEnterFormTitleText"
							:show-more-button="true"
							:social-notification-text="socialNotificationText"
							:show-go-to-box-block="showVibeAuthPromoBlock ? false : showGoToBoxBlock"
							:show-set-box-url-button="showVibeAuthPromoBlock ? false : isDesktopLogin"
							:show-vibe-auth-promo-block="showVibeAuthPromoBlock"
							:gmail-hint-variant="showVibeAuthPromoBlock ? 'long' : 'short'"
							:vibecode-continue-terms-html="vibecodeContinueTermsHtml"
							@indicate-b24-address-click="onIndicateB24AddressClick"
							@indicate-b24-address-redirect="onIndicateB24AddressRedirect"
							@vibe-auth-promo-button-click="onVibeRegisterClick"
							@sso-click="onSsoClick"
							@qr-click="onQrBtnClick"
							@login-change="onLoginChange"
							@social-click="onSocialButtonClick"
							@continue-click="onLoginContinueClick"
							@other-socials-click="onOtherSocialsClick"
							@login-enter-key-up="onLoginContinueClick"
						/>

						<PasswordEnterForm
							v-if="isPasswordEnterStep"
							ref="passwordEnterForm"
							class="b24net-logging-in__password-form"
							:account="currentAccount.toDto()"
							:password-value="password"
							:show-qr-code-button="false"
							:password-error="passwordError"
							:is-waiting="isProcessingPasswordEnter"
							:can-change-account="hasMultipleAccounts"
							:is-remember-me-checked="isRememberMeChecked"
							:captcha-settings="webLoginCaptchaSettingsForPasswordEnterForm"
							:is-captcha-failed="isWebLoginCaptchaFailed"
							:captcha-error="webLoginCaptchaError"
							test-id-prefix="b24network-password-enter-form"
							@password-change="onPasswordChange"
							@remember-me-change="onRememberMeChange"
							@continue-click="onPasswordContinueClick"
							@account-change-click="onAccountChangeClick"
							@forgot-password-click="onForgotPasswordClick"
							@captcha-result-change="onWebLoginCaptchaResultChange"
						/>

						<LegalLinks
							class="b24net-page-base__legal-links"
							:links="legalLinksForCaptchaOnPage"
							:type-of-captcha-on-page="typeOfCaptchaOnPage"
						/>
					</div>

					<BasePopup
						align-with-target-vertical="center"
						align-with-target-horizontal="center"
						popup-class-name="popup-window--island-wrapper"
						:can-close-on-esc="false"
						:need-close-button="true"
						:offset-from-target-px="10"
						:target-node-rect="popupAnchorRect"
						:is-visible="isSocialAccountListVisible"
						@popup-close="onSocialAccountListPopupClose"
					>
						<div class="b24net-island">
							<AccountList
								v-if="currentSocialAccounts.length > 0"
								class="b24net-logging-in__social-accounts"
								:show-new-account-link="false"
								:accounts="currentSocialAccounts"
								:title-text="socialSvcAccountTitle"
								:description-text="socialSvcAccountDescription"
								@account-list-click="onSocialAccountItemClick"
							/>

							<div v-if="isUserHasEmail" class="b24net-logging-in__social-accounts-footer">
								<TextButton
									button-type="${TextButtonType.LINK}"
									@click="onCreatePasswordFromSocServClick"
								>
									{{ $Bitrix.Loc.getMessage('EXT_B24NET_LOGGING_IN_CREATE_PASSORD_SOCSERV_ACCOUNT') }}
								</TextButton>
							</div>
						</div>
					</BasePopup>

					<BasePopup
						align-with-target-vertical="center"
						align-with-target-horizontal="center"
						:min-width="350"
						:max-width="465"
						:border-radius="16"
						:can-close-on-esc="false"
						:need-close-button="true"
						:offset-from-target-px="10"
						:target-node-rect="popupAnchorRect"
						:is-visible="isAccountsListVisible"
						@popup-close="onAccountListPopupClose"
					>
						<AccountList
							v-if="hasAccounts && currentAccount"
							class="b24net-logging-in__accounts-history"
							:accounts="accountsHistoryDtos"
							:selected-account-id="currentAccount.toId()"
							@account-list-click="onAccountItemClick"
							@account-delete-click="onDeleteAccountFromHistoryClick"
							@account-add-click="onB24CreateClick"
						/>
					</BasePopup>

					<BasePopup
						align-with-target-vertical="bottom"
						align-with-target-horizontal="center"
						:padding="18"
						:border-radius="16"
						:can-close-on-esc="false"
						:need-close-button="false"
						:offset-from-target-px="10"
						:is-visible="isExtraSocialsPopupVisible"
						:target-node-rect="extraSocialsButtonRect"
						:popup-width-px="popupWidthForExtraSocialServices"
						@popup-close="onExtraSocialsPopupClose"
					>
						<div class="b24net-logging-in__social-btn-wrapper">
							<TextButton
								class="b24net-login-enter-form__social-btn"
								v-tooltip="{ text: 'Single Sign-on' }"
								button-type="${TextButtonType.SSO}"
								@click="onSsoClick"
							>
								SSO
							</TextButton>

							<IconButton
								v-for="socialService in socialServicesForPopup"
								v-tooltip="{ text: socialService.NAME }"
								key="socialService.ID"
								class="b24net-logging-in__social-btn"
								:has-border="false"
								:button-type="getButtonTypeForSocialServiceId(socialService.ID)"
								@click="onSocialButtonClick"
							/>
						</div>
					</BasePopup>

					<BasePopup
						align-with-target-vertical="bottom"
						align-with-target-horizontal="center"
						popup-class-name="b24net-logging-in__qr-code-popup"
						:padding="0"
						:border-radius="16"
						:can-close-on-esc="false"
						:need-close-button="true"
						:is-visible="isQrPopupVisible"
						@popup-close="onQrPopupClose"
					>
						<QrScanForm
							v-show="isQrCodeVisible"
							:is-authenticated="isAuthenticatedByQr"
							@mobile-app-store-click="onMobileAppStoreClick"
						>
							<div ref="qrCodeInPopup"></div>
						</QrScanForm>
					</BasePopup>
				</div>

				<div class="b24net-island-with-sidebar__sidebar">
					<Transition name="b24net-logging-in__qr-code--slide">
						<QrScanForm
							v-show="isQrCodeVisible"
							class="b24net-logging-in__qr-code"
							:is-authenticated="isAuthenticatedByQr"
							@mobile-app-store-click="onMobileAppStoreClick"
						>
							<div id="b24network-auth-qrcode" ref="qrCode"></div>
						</QrScanForm>
					</Transition>
				</div>
			</div>
		</div>
	`,
};
